// Dashboard analytics are snapshots derived from records already returned by the
// existing APIs. In particular, an "active user" is a user whose most recent
// `lastSeen` timestamp falls on a displayed UTC calendar day; this is not an
// event-history or historical DAU endpoint.
const DAY_MS = 24 * 60 * 60 * 1000;

const validTime = value => {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const utcDayStart = value => {
  const date = new Date(value);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const dayKey = time => new Date(time).toISOString().slice(0, 10);

export const countActiveServices = (services = []) => (Array.isArray(services) ? services : [])
  .filter(service => service && service.status !== 'banned').length;

export const countOpenTickets = (tickets = []) => (Array.isArray(tickets) ? tickets : [])
  .filter(ticket => ticket && ticket.status === 'open').length;

export const buildActiveUserAnalytics = (users = [], rangeDays = 7, now = new Date()) => {
  const days = [7, 30, 90].includes(Number(rangeDays)) ? Number(rangeDays) : 7;
  const end = utcDayStart(now);
  const start = end - (days - 1) * DAY_MS;
  const counts = new Map();

  (Array.isArray(users) ? users : []).forEach(user => {
    const time = validTime(user && user.lastSeen);
    if (time === null) return;
    const bucketTime = utcDayStart(time);
    if (bucketTime < start || bucketTime > end) return;
    const key = dayKey(bucketTime);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const buckets = Array.from({ length: days }, (_, index) => {
    const time = start + index * DAY_MS;
    const key = dayKey(time);
    return {
      key,
      label: new Date(time).toLocaleDateString(undefined, days <= 7
        ? { weekday: 'short', timeZone: 'UTC' }
        : { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      value: counts.get(key) || 0,
    };
  });
  // Compare equal-length adjacent periods. Odd ranges intentionally omit only
  // the oldest extra bucket (7 days => preceding 3 vs most recent 3).
  const comparisonDays = Math.floor(days / 2);
  const recentStart = days - comparisonDays;
  const previousStart = recentStart - comparisonDays;
  const previous = buckets.slice(previousStart, recentStart).reduce((sum, bucket) => sum + bucket.value, 0);
  const current = buckets.slice(recentStart).reduce((sum, bucket) => sum + bucket.value, 0);
  const trendPercent = previous === 0
    ? (current === 0 ? 0 : null)
    : Math.round(((current - previous) / previous) * 1000) / 10;

  return {
    buckets,
    total: buckets.reduce((sum, bucket) => sum + bucket.value, 0),
    trendPercent,
    comparisonDays,
    previousPeriodTotal: previous,
    recentPeriodTotal: current,
  };
};

export const groupReportLogs = (logs = []) => {
  const grouped = (Array.isArray(logs) ? logs : [])
    .filter(log => log.action === 'REPORT' && log.relatedId)
    .reduce((acc, log) => {
    const key = log.relatedId || 'UNKNOWN';
    if (!acc[key]) acc[key] = { relatedId: key, relatedType: log.relatedType, logs: [] };
    acc[key].logs.push(log);
    return acc;
  }, {});

  return Object.values(grouped).map(group => {
    const openLogs = group.logs.filter(log => log.status === 'OPEN');
    const sorted = group.logs.slice().sort((a, b) => (validTime(b.createdAt) || 0) - (validTime(a.createdAt) || 0));
    return {
      ...group,
      openCount: openLogs.length,
      total: group.logs.length,
      groupStatus: openLogs.length > 0 ? 'OPEN' : 'DONE',
      latest: sorted[0],
    };
  }).sort((a, b) => (validTime(b.latest && b.latest.createdAt) || 0) - (validTime(a.latest && a.latest.createdAt) || 0));
};

export const summarizeReportGroups = groups => ['requests', 'users', 'feeds'].reduce((summary, type) => {
  const matching = (Array.isArray(groups) ? groups : []).filter(group => group.relatedType === type);
  summary[type] = {
    total: matching.length,
    open: matching.filter(group => group.groupStatus === 'OPEN').length,
  };
  return summary;
}, {});

export const summarizeBlocks = (users = []) => {
  const safeUsers = Array.isArray(users) ? users : [];
  const usersWithField = safeUsers.filter(user => user && Array.isArray(user.blockedUsers));
  return {
    available: usersWithField.length > 0,
    relationships: usersWithField.reduce((sum, user) => sum + user.blockedUsers.length, 0),
    blockingUsers: usersWithField.filter(user => user.blockedUsers.length > 0).length,
  };
};

export const recentActivity = (logs = [], limit = 4) => (Array.isArray(logs) ? logs : [])
  .filter(log => validTime(log && log.createdAt) !== null)
  .slice()
  .sort((a, b) => validTime(b.createdAt) - validTime(a.createdAt))
  .slice(0, limit)
  .map(log => ({
    id: log._id || `${log.action || 'event'}-${log.createdAt}`,
    actor: (log.user && (log.user.name || log.user.phone)) || 'System event',
    detail: log.description || log.message || log.action || 'Activity recorded',
    action: log.action || 'EVENT',
    createdAt: log.createdAt,
  }));
